/**
 * Moderation API hooks (admin queue + claim + resolve, promotional
 * events CRUD).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackAdminEvent } from '../lib/posthog.js';
import { apiFetch } from './client.js';

export interface ReportRow {
  id: string;
  reporterUserId: string;
  reportedUserId: string;
  reason: string;
  note: string | null;
  status: string;
  claimedByAdminId: string | null;
  claimedAt: string | null;
  createdAt: string;
  relatedReportCount: number;
}

export interface PromoEventRow {
  id: string;
  name: string;
  status: string;
  scheduleType: string;
  segmentType: string;
  utcStartsAt: string | null;
  utcEndsAt: string | null;
  localDate: string | null;
  durationHours: number | null;
  perks: string[];
  createdAt: string;
}

export const queueKeys = {
  all: ['admin-queue'] as const,
  list: () => [...queueKeys.all, 'list'] as const,
};

export const promoKeys = {
  all: ['promo-events'] as const,
  list: () => [...promoKeys.all, 'list'] as const,
};

// ─── Queue ───────────────────────────────────────────────────────────

export function useQueueQuery() {
  return useQuery({
    queryKey: queueKeys.list(),
    queryFn: () =>
      apiFetch<{ reports: ReportRow[] }>({
        method: 'GET',
        url: '/admin/queue',
      }),
  });
}

export function useClaimReportMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      apiFetch<{ claimed: boolean }>({
        method: 'POST',
        url: `/admin/queue/${reportId}/claim`,
      }),
    onSuccess: (_data, reportId) => {
      void qc.invalidateQueries({ queryKey: queueKeys.list() });
      trackAdminEvent('admin.report.claimed', { reportId });
    },
  });
}

export function useResolveReportMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      reportId: string;
      resolution: 'dismiss' | 'warn' | 'soft_pause' | 'ban';
      note?: string;
    }) =>
      apiFetch<{ status: string }>({
        method: 'POST',
        url: `/admin/queue/${args.reportId}/resolve`,
        body: { resolution: args.resolution, ...(args.note ? { note: args.note } : {}) },
      }),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: queueKeys.list() });
      // Resolution breakdown — dismiss / warn / soft_pause / ban —
      // is the most useful funnel slice for understanding queue
      // throughput. The reportId is opaque + non-PII.
      trackAdminEvent('admin.report.resolved', {
        reportId: args.reportId,
        resolution: args.resolution,
      });
    },
  });
}

// ─── Promotional events ──────────────────────────────────────────────

export function usePromoEventsQuery() {
  return useQuery({
    queryKey: promoKeys.list(),
    queryFn: () =>
      apiFetch<{ events: PromoEventRow[] }>({
        method: 'GET',
        url: '/admin/promotional-events',
      }),
  });
}

export interface CreatePromoEventBody {
  name: string;
  description?: string;
  perks: string[];
  scheduleType: 'utc_window' | 'local_time_window';
  utcStartsAt?: string;
  utcEndsAt?: string;
  localDate?: string;
  localStartTime?: string;
  durationHours?: number;
  segmentType: 'global' | 'countries';
  segmentCountries?: string[];
}

export function useCreatePromoEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePromoEventBody) =>
      apiFetch<{ eventId: string; status: string; perksAfterStrip: string[] }>({
        method: 'POST',
        url: '/admin/promotional-events',
        body,
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: promoKeys.list() });
      trackAdminEvent('admin.promo_event.created', { eventId: data.eventId });
    },
  });
}

export function useCancelPromoEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { eventId: string; revokeImmediately?: boolean }) =>
      apiFetch<{ cancelled: boolean }>({
        method: 'POST',
        url: `/admin/promotional-events/${args.eventId}/cancel`,
        body: { revokeImmediately: args.revokeImmediately ?? false },
      }),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: promoKeys.list() });
      trackAdminEvent('admin.promo_event.cancelled', {
        eventId: args.eventId,
        revokeImmediately: args.revokeImmediately ?? false,
      });
    },
  });
}
